import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../Header';
import './AuthPage.css';

const AuthPage = () => {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const { login, signup, resetPassword } = useAuth();
  const navigate = useNavigate();

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isReset = mode === 'reset';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (isReset) {
      if (!email) {
        setError('Please enter your email');
        return;
      }
      setLoading(true);
      try {
        await resetPassword(email);
        setInfo('Password reset email sent. Check your inbox (and spam folder).');
      } catch (err) {
        setError(err.message || 'Failed to send reset email. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validation
    if (!email || !password) {
      setError('Please fill in all required fields');
      return;
    }

    if (isRegister) {
      if (!displayName) {
        setError('Please enter a display name');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password, displayName);
      }
      navigate('/game');
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
    setPassword('');
    setConfirmPassword('');
  };

  const submitLabel = isReset
    ? 'SEND RESET LINK'
    : isLogin
    ? 'ENTER THE GAME'
    : 'JOIN THE COUNCIL';

  return (
    <div className="auth-container">
      <Header showUserSection={false} />
      <div className="auth-background">
        <div className="chess-pattern"></div>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <div className="chess-icon">
            <svg viewBox="0 0 45 45" className="king-icon">
              <g fill="none" fillRule="evenodd" stroke="#6B8FFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 22.5,11.63 L 22.5,6" strokeLinejoin="miter"/>
                <path d="M 20,8 L 25,8" strokeLinejoin="miter"/>
                <path d="M 22.5,25 C 22.5,25 27,17.5 25.5,14.5 C 25.5,14.5 24.5,12 22.5,12 C 20.5,12 19.5,14.5 19.5,14.5 C 18,17.5 22.5,25 22.5,25"/>
                <path d="M 12.5,37 C 18,40.5 27,40.5 32.5,37 L 32.5,30 C 32.5,30 41.5,25.5 38.5,19.5 C 34.5,13 25,16 22.5,23.5 L 22.5,27 L 22.5,23.5 C 20,16 10.5,13 6.5,19.5 C 3.5,25.5 12.5,30 12.5,30 L 12.5,37"/>
                <path d="M 12.5,30 C 18,27 27,27 32.5,30" fill="none"/>
                <path d="M 12.5,33.5 C 18,30.5 27,30.5 32.5,33.5" fill="none"/>
                <path d="M 12.5,37 C 18,34 27,34 32.5,37" fill="none"/>
              </g>
            </svg>
          </div>
          <h1 className="auth-title">LIVING CHESS</h1>
          <p className="auth-subtitle">
            {isReset ? 'Reset your password' : 'Welcome to Shadows'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠</span>
              {error}
            </div>
          )}

          {info && (
            <div className="error-message" style={{ background: 'rgba(107, 143, 255, 0.15)', borderColor: 'rgba(107, 143, 255, 0.4)', color: '#cfd9ff' }}>
              <span className="error-icon">✓</span>
              {info}
            </div>
          )}

          {isRegister && (
            <div className="form-group">
              <label htmlFor="displayName">Display Name</label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your chess name"
                disabled={loading}
                required={isRegister}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
              required
            />
          </div>

          {!isReset && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading}
                required
              />
            </div>
          )}

          {isRegister && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={loading}
                required={isRegister}
              />
            </div>
          )}

          {isLogin && (
            <div className="auth-toggle" style={{ marginTop: '-8px', marginBottom: '8px' }}>
              <button
                type="button"
                onClick={() => switchMode('reset')}
                className="toggle-button"
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>
          )}

          {isRegister && (
            <p className="auth-consent">
              By registering, you agree to our{' '}
              <Link to="/privacy" className="auth-consent-link">Privacy Policy</Link>.
            </p>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="loading-spinner"></span>
            ) : (
              submitLabel
            )}
          </button>

          <div className="auth-toggle">
            {isReset ? (
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="toggle-button"
                disabled={loading}
              >
                Back to login
              </button>
            ) : (
              <button
                type="button"
                onClick={() => switchMode(isLogin ? 'register' : 'login')}
                className="toggle-button"
                disabled={loading}
              >
                {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
              </button>
            )}
          </div>

          <div className="auth-toggle">
            <button
              type="button"
              onClick={() => navigate('/spectate')}
              className="toggle-button"
              disabled={loading}
            >
              Just watching? Spectate a live game →
            </button>
          </div>

          <div className="auth-toggle">
            <Link to="/privacy" className="toggle-button auth-privacy-link">
              Privacy Policy
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;
